// =============================================================================
// io/billing/checkout.ts — start a Stripe checkout, or open the billing portal.
// -----------------------------------------------------------------------------
// Relocated from the old client's `startCheckout` / `openBillingPortal`
// (app/index.html:39890 and :39951). #65.
//
// ── 🔴 NONE OF THE NINE MONEY HAZARDS LIVE HERE ──
//
// `docs/STRIPE-BILLING-PLAN.md` §5 lists nine ways a customer can pay and get
// nothing, or keep access without paying. Every one of them is in the WEBHOOK or
// the checkout Edge Function — dedupe-before-processing, the user-mapping paths,
// the duplicate-purchase pre-check, orphaned rows, unrecognised prices, refund
// durability, event ordering. Those functions exist and are live.
//
// This module's whole job is to call them correctly. Its own risks are small and
// there are exactly three:
//
//   1. never offer to buy when we do not know what they already have
//   2. never redirect a Founder click to a standard-price page
//   3. never claim success on the redirect — entitlement comes from the webhook
//
// (1) is enforced by the card, (2) by `soldOut` below, (3) by the return handler.
//
// ── ⚠ THIS MODULE DOES NOT NAVIGATE ──
//
// The old client assigns `window.location.href` inside the fetch. Here the URL
// is RETURNED and the caller navigates, so the decision to leave the page is
// visible where the button is and the whole path is testable without a browser.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { ENV } from '../../env';
import { reportFailure } from '../diagnostics/errorLog';

/** Which Edge Functions. Stripe is the provider — Polar and Paddle both refused. */
const FN = { checkout: 'stripe-checkout', portal: 'stripe-portal' } as const;

export type BillingInterval = 'annual' | 'monthly';

export type CheckoutResult =
  /** Send the browser here. */
  | { ok: true; url: string }
  /**
   * 🚨 They pressed a FOUNDER button and the hundred seats had gone.
   *
   * The server builds a regular-price session rather than erroring — a sold-out
   * tier must never turn the primary buy button into a dead end — but following
   * it silently would show a $290 page to somebody who just clicked $180. So the
   * caller must stop and say so. Nobody has been charged; the unused session
   * expires on its own in half an hour.
   */
  | { ok: false; soldOut: true; error: string }
  | { ok: false; soldOut?: false; error: string };

const SOLD_OUT = 'The 100 Founder seats have just sold out — you have NOT been '
  + 'charged. The standard plan is still available.';

async function post(sb: SupabaseClient, fn: string, body: unknown): Promise<CheckoutResult> {
  const { data: sess } = await sb.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) return { ok: false, error: 'Sign in first.' };

  let res: Response;
  try {
    res = await fetch(`${ENV.config.supabaseUrl}/functions/v1/${fn}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: ENV.config.supabaseAnonKey,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (e) {
    // 🚨 THROUGH THE REPORTER, NOT AROUND IT. A guard in the suite caught the
    // first version of this line building its own message — and billing is the
    // worst place to lose one: a customer saying "the buy button did nothing" is
    // unanswerable if the failure never reached the activity log.
    return { ok: false, error: reportFailure(`billing (${fn})`, e, 'Could not reach the billing service.') };
  }

  const out = await res.json().catch(() => ({})) as Record<string, unknown>;

  // ⚠ CHECKED BEFORE `res.ok`. The server answers 200 with a regular-price
  // session in this case — it is a successful response carrying a fact the user
  // has to be told, not an error.
  if (out['foundersSoldOut'] === true) return { ok: false, soldOut: true, error: SOLD_OUT };

  const url = typeof out['url'] === 'string' ? out['url'] : '';
  if (!res.ok || !url) {
    const msg = typeof out['error'] === 'string' && out['error'] ? out['error'] : null;
    return { ok: false, error: msg ?? 'Could not start checkout.' };
  }
  return { ok: true, url };
}

/**
 * Begin a purchase.
 *
 * ⚠ The payload is `{ interval, founder }`. An older shape, `{ plan }`, is on
 * the launch checklist as a thing to change — it is already changed here.
 */
export function startCheckout(
  sb: SupabaseClient, args: { interval: BillingInterval; founder: boolean },
): Promise<CheckoutResult> {
  return post(sb, FN.checkout, { interval: args.interval, founder: !!args.founder });
}

/** Stripe's own portal — cancel, change card, download invoices. */
export async function openBillingPortal(sb: SupabaseClient): Promise<CheckoutResult> {
  const r = await post(sb, FN.portal, undefined);
  if (!r.ok && !r.soldOut && r.error === 'Could not start checkout.') {
    return { ok: false, error: 'Could not open the billing portal.' };
  }
  return r;
}
